import { ThemeEvents } from '@theme/events';
import { onDocumentReady } from '@theme/utilities';

/*
 -----------------------------------------------------------------------------
 
 REBUY RECOMMENDED — assets/rebuy-recommended.js
 • data-rebuy-analytics-tracking   → SDK productView delegation (analytics)
 • data-rebuy-reporting-line-items → appends rebuy_reporting=1 so sections/rebuy-product-cards.liquid emits cart properties

 -----------------------------------------------------------------------------
*/

/** @type {WeakMap<HTMLElement, AbortController>} */
const rebuyRecommendedProductViewTrackingAbort = new WeakMap();

/**
 * Rebuy SDK `tracking.productView` may reject (e.g. internal `logger` undefined) unless fully configured.
 *
 * @param {unknown} sdk
 * @param {string} productId
 */
function invokeSdkProductViewTracking(sdk, productId) {
  const boxed = /** @type {{ tracking?: { productView?: (id: string) => unknown } } } */ (
    /** @type {unknown} */ (sdk)
  );
  const tracking = boxed?.tracking;
  const fn = tracking?.productView;
  if (typeof fn !== 'function') return;
  try {
    const result = fn.call(tracking, productId);
    if (result != null && typeof /** @type {{ then?: (fn: () => unknown) => unknown }} */ (result).then === 'function') {
      /** @type {Promise<unknown>} */ (result).catch(() => {});
    }
  } catch {
    /* synchronous SDK errors — remove with Rebuy SDK analytics teardown */
  }
}

/**
 * @param {HTMLElement} mount
 * @param {unknown} sdk
 */
function bindRebuyRecommendedProductViewTracking(mount, sdk) {
  rebuyRecommendedProductViewTrackingAbort.get(mount)?.abort();
  const ac = new AbortController();
  rebuyRecommendedProductViewTrackingAbort.set(mount, ac);
  mount.addEventListener(
    'click',
    (/** @type {MouseEvent} */ e) => {
      const target = e.target;
      const raw = target instanceof Element ? target.closest('[data-rebuy-card][data-product-id]') : null;
      const card =
        raw instanceof HTMLElement && raw.hasAttribute('data-rebuy-card') && mount.contains(raw)
          ? raw
          : null;
      const pid = card ? rebuyCardProductId(card) : '';
      if (!pid) return;

      if (target instanceof Element && target.closest('.resource-card__quick-add-slot')) return;

      invokeSdkProductViewTracking(sdk, String(pid));
    },
    { signal: ac.signal }
  );
}

const REBUY_SECTION_ID = 'rebuy-product-cards';

/** @type {Map<string, Promise<any>>} */
const sdkCache = new Map();

/** @type {WeakMap<HTMLElement, number>} */
const rebuyMountHydrateGeneration = new WeakMap();

/**
 * Per API key: caches the SDK load promise; rejects evict so the next attempt can retry.
 * SDK is vendored as assets/rebuy-core-sdk.esm.js and mapped in snippets/scripts.liquid (@rebuy/core-sdk).
 *
 * @param {string} apiKey
 * @returns {Promise<any>}
 */
function getSdk(apiKey) {
  const cached = sdkCache.get(apiKey);
  if (cached) return cached;

  // Resolved at runtime via import map (snippets/scripts.liquid) → Shopify CDN asset
  // @ts-expect-error -- bare specifier not in package.json; browser resolves via importmap
  const loadImport = import('@rebuy/core-sdk');

  const pending = loadImport
    .then((m) => {
      const Sdk = m.RebuySDK;
      return new Sdk({ apiKey });
    })
    .catch((err) => {
      sdkCache.delete(apiKey);
      throw err;
    });

  sdkCache.set(apiKey, pending);
  return pending;
}

/**
 * @param {string} html
 * @returns {Element | undefined}
 */
function findRebuySectionRoot(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const byClass = doc.querySelector('.shopify-section.rebuy-product-cards-section');
  if (byClass) {
    return byClass;
  }
  return doc.querySelector('[data-product-count]')?.closest('.shopify-section') ?? undefined;
}

/**
 * @param {unknown} id
 * @returns {string}
 */
function normProductId(id) {
  if (id == null) return '';
  return typeof id === 'bigint' ? id.toString() : String(id);
}

/**
 * @param {Element} el
 * @returns {string}
 */
function rebuyCardProductId(el) {
  return el instanceof HTMLElement ? normProductId(el.dataset.productId) : '';
}

/**
 * @param {HTMLElement} mount
 */
function failRebuyRecommendedMount(mount) {
  rebuyRecommendedProductViewTrackingAbort.get(mount)?.abort();
  mount.innerHTML = '';
  mount.hidden = true;
  const group = mount.closest('[data-rebuy-recommended-group]');
  if (group instanceof HTMLElement) group.hidden = true;
}

/**
 * @param {HTMLElement} mount
 */
function resetCartDrawerRebuySkeletonFromTemplate(mount) {
  const group = mount.closest('[data-rebuy-recommended-group]');
  if (!(group instanceof HTMLElement)) return;
  if (!mount.closest('.cart-drawer__dialog')) return;

  const tmpl = group.querySelector('#cart-drawer-rebuy-skeleton-template');
  if (!(tmpl instanceof HTMLTemplateElement)) return;

  rebuyRecommendedProductViewTrackingAbort.get(mount)?.abort();
  mount.replaceChildren(document.importNode(tmpl.content, true));
}

/**
 * @param {HTMLElement} mount
 * @returns {Promise<void>}
 */
async function hydrateRebuyRecommendedMount(mount) {
  const apiKey = mount.dataset.apiKey;
  const collectionPath = mount.dataset.collectionPath;
  const productId = mount.dataset.productId;
  const limitAttr = Number.parseInt(String(mount.dataset.limit), 10);
  const rebuyDataSource = mount.dataset.rebuyDataSource;
  const rebuyDataSourceID = mount.dataset.rebuyDataSourceId;

  if (!apiKey || !collectionPath || !productId) {
    failRebuyRecommendedMount(mount);
    return;
  }

  resetCartDrawerRebuySkeletonFromTemplate(mount);

  const prevGen = rebuyMountHydrateGeneration.get(mount) ?? 0;
  const gen = prevGen + 1;
  rebuyMountHydrateGeneration.set(mount, gen);

  mount.hidden = false;
  const group = mount.closest('[data-rebuy-recommended-group]');
  if (group instanceof HTMLElement) group.hidden = false;

  try {
    const sdk = await getSdk(apiKey);
    if (gen !== rebuyMountHydrateGeneration.get(mount)) return;

    let params;
    const defaultParams = {
      shopify_product_ids: productId,
      filter_oos: 'yes',
      variant_price_greater_than: '0.00',
      fields: "id",
      bust_cache: 'yes' 
    };


    switch (rebuyDataSource) {
      case 'fetchFromDataSource':
         params = [rebuyDataSourceID, {
          bust_cache: 'yes' 
         }];
        break;
      default:
        params = [defaultParams];
        break;
    }

    let recs = await sdk.products[rebuyDataSource](...params);

    if (gen !== rebuyMountHydrateGeneration.get(mount)) return;

    if (!recs?.length) {
      failRebuyRecommendedMount(mount);
      return;
    }

    const anchorId = normProductId(productId);
    const seenIds = /** @type {Set<string>} */ (new Set());

    recs = recs
      .filter(
        /** @param {{ id?: unknown }} p */
        (p) => {
          const sid = normProductId(p?.id);
          if (!sid || sid === anchorId || seenIds.has(sid)) return false;
          seenIds.add(sid);
          return true;
        }
      )
      .slice(0, limitAttr);

    const ids = recs.map(
      /** @param {{ id?: unknown }} p */
      (p) => normProductId(p.id)
    );

    if (ids.length === 0) {
      failRebuyRecommendedMount(mount);
      return;
    }

    if (gen !== rebuyMountHydrateGeneration.get(mount)) return;

    params = ids
      .map(
        /** @param {string} id */
        (id) => `filter.p.m.custom.product_id=${id}`
      )
      .join('&');
    const sep = collectionPath.includes('?') ? '&' : '?';
    const filterQuery = `${collectionPath}${sep}${params}`;
    const reportingOn = mount.dataset.rebuyReportingLineItems === 'true';
    let sectionUrl = `${filterQuery}&section_id=${encodeURIComponent(REBUY_SECTION_ID)}`;
    if (reportingOn) sectionUrl += '&rebuy_reporting=1';

    const res = await fetch(sectionUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
    const html = await res.text();
    if (gen !== rebuyMountHydrateGeneration.get(mount)) return;

    const sectionRoot = findRebuySectionRoot(html);

    if (!sectionRoot) {
      failRebuyRecommendedMount(mount);
      return;
    }

    const container = sectionRoot.querySelector('[data-product-count]');
    const cards = container ? [...container.querySelectorAll('[data-rebuy-card]')] : [];

    if (container) {
      const idSet = new Set(ids);
      ids.forEach(
        /** @param {string} id */
        (id) => {
          const card = cards.find((el) => rebuyCardProductId(el) === id);
          if (card) container.appendChild(card);
        }
      );
      container.querySelectorAll('[data-rebuy-card]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (!idSet.has(rebuyCardProductId(el))) el.remove();
      });
    }

    const renderedCards = container ? container.querySelectorAll('[data-rebuy-card]') : [];
    if (!container || renderedCards.length === 0) {
      failRebuyRecommendedMount(mount);
      return;
    }

    if (gen !== rebuyMountHydrateGeneration.get(mount)) return;

    mount.innerHTML = sectionRoot.innerHTML;
    if (mount.dataset.rebuyAnalyticsTracking === 'true') bindRebuyRecommendedProductViewTracking(mount, sdk);
  } catch {
    failRebuyRecommendedMount(mount);
  }
}

/**
 * @param {{ fromVariantUpdate?: boolean }} [options]
 */
function hydrateRebuyRecommendations(options = {}) {
  const skipDrawerMountsFromVariantEvent = options.fromVariantUpdate === true;
  document.querySelectorAll('[data-rebuy-mount]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (skipDrawerMountsFromVariantEvent && el.closest('.cart-drawer__dialog')) return;
    void hydrateRebuyRecommendedMount(el);
  });
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  queueMicrotask(() => hydrateRebuyRecommendations());
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variantUpdate = /** @type {import('@theme/events').VariantUpdateEvent} */ (event);
  const morphMain = Boolean(variantUpdate.detail?.data?.morphedMain);
  const newProductId = variantUpdate.detail?.data?.newProduct?.id;
  const productId = variantUpdate.detail?.data?.productId;
  const variantId = variantUpdate.detail?.resource?.id;
  // combined listing + restore: follow-up event can omit newProduct despite morphed main / skeleton
  if (!morphMain && !newProductId && !productId && !variantId) return;
  void hydrateRebuyRecommendations({ fromVariantUpdate: true });
});

onDocumentReady(hydrateRebuyRecommendations)